import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { 
    getLeadAccountDailyStatsById, 
    createLeadAccountDailyStats, 
    updateLeadAccountDailyStats,
    getLeadAccounts 
} from '../api';
import { logError } from '../api';
import { isAdmin } from '../services/AuthService';

const LeadAccountDailyStatsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        leadAccountId: '',
        statDate: new Date().toISOString().split('T')[0],
        leadCount: 0
    });

    const [leadAccounts, setLeadAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadLeadAccounts();
        if (isEdit) {
            loadStats();
        }
    }, [id, isEdit]);

    const loadLeadAccounts = async () => {
        try {
            const response = await getLeadAccounts(1, 1000, false);
            setLeadAccounts(response.items || []);
        } catch (error) {
            logError(error);
            setError('Error al cargar las cuentas de lead');
        }
    };

    const loadStats = async () => {
        try {
            setLoading(true);
            const stats = await getLeadAccountDailyStatsById(id);
            setFormData({
                leadAccountId: stats.leadAccountId || '',
                statDate: stats.statDate ? stats.statDate.split('T')[0] : '',
                leadCount: stats.leadCount || 0
            });
        } catch (error) {
            logError(error);
            setError('Error al cargar las estadísticas');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value) || 0 : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.leadAccountId || !formData.statDate) {
            setError('Por favor complete todos los campos obligatorios');
            return;
        }

        // Validar permisos de edición
        if (isEdit) {
            const statDate = new Date(formData.statDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            statDate.setHours(0, 0, 0, 0);
            
            const isToday = statDate.getTime() === today.getTime();
            
            if (!isAdmin() && !isToday) {
                setError('Solo se pueden editar estadísticas del día actual');
                return;
            }
        }

        try {
            setLoading(true);
            
            const statsData = {
                ...formData,
                statDate: new Date(formData.statDate).toISOString()
            };

            if (isEdit) {
                await updateLeadAccountDailyStats(id, statsData);
            } else {
                await createLeadAccountDailyStats(statsData);
            }

            navigate('/lead-account-history');
        } catch (error) {
            logError(error);
            setError(error.message || 'Error al guardar las estadísticas');
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEdit) {
        return <div>Cargando...</div>;
    }

    return (
        <Container className="my-4">
            <Row>
                <Col>
                    <h2>{isEdit ? 'Editar' : 'Crear'} Estadísticas Diarias</h2>
                    
                    {error && <Alert variant="danger">{error}</Alert>}
                    
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Cuenta de Lead *</Form.Label>
                                    <Form.Select
                                        name="leadAccountId"
                                        value={formData.leadAccountId}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Seleccionar cuenta</option>
                                        {leadAccounts.map(account => (
                                            <option key={account.id} value={account.id}>
                                                {account.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Fecha *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="statDate"
                                        value={formData.statDate}
                                        onChange={handleInputChange}
                                        required
                                        max={isAdmin() ? undefined : new Date().toISOString().split('T')[0]}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Cantidad de Leads</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="leadCount"
                                        value={formData.leadCount}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex gap-2">
                            <Button 
                                type="submit" 
                                variant="primary" 
                                disabled={loading}
                            >
                                {loading ? 'Guardando...' : 'Guardar'}
                            </Button>
                            <Button 
                                type="button" 
                                variant="secondary" 
                                onClick={() => navigate('/lead-account-history')}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default LeadAccountDailyStatsForm;